var _excluded = ["children", "gutter", "minSlideWidth", "maxSlideWidth", "responsive"];
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import React from "react";
import PropTypes from "prop-types";
import classesPropType from "@xo-union/classes-prop-type";
import noop from "lodash/noop";
import BaseCarousel from "../BaseCarousel/index.js";
import useResizeObserver from "../useResizeObserver/index.js";
import getSlideCount from "../getSlideCount/index.js";
import { humanTermType } from "../humanTerms.js";
import getDefaultAlignment from "../getDefaultAlignment/index.js";

/**
 * Carousel container.
 */
var Carousel = function Carousel(props) {
  var children = props.children,
    gutter = props.gutter,
    minSlideWidth = props.minSlideWidth,
    maxSlideWidth = props.maxSlideWidth,
    responsive = props.responsive,
    restProps = _objectWithoutProperties(props, _excluded);
  var resizeObserverProps = {
    gutter: gutter,
    minSlideWidth: minSlideWidth,
    maxSlideWidth: maxSlideWidth,
    responsive: responsive
  };
  var totalSlidesCount = getSlideCount(children);
  var containerRef = React.useRef();
  var resizeObserver = useResizeObserver(resizeObserverProps, {
    containerRef: containerRef,
    totalSlidesCount: totalSlidesCount
  });
  var visibleSlidesCount = resizeObserver.visibleSlidesCount,
    extraPixels = resizeObserver.extraPixels,
    slideWidth = resizeObserver.slideWidth;
  var slideWidthValue = "".concat(slideWidth, "px");
  var isReadyToRender = !!slideWidth;
  return /*#__PURE__*/React.createElement(BaseCarousel, _extends({}, restProps, {
    gutter: gutter,
    internals: {
      containerRef: containerRef,
      totalSlidesCount: totalSlidesCount,
      slideWidthValue: slideWidthValue,
      extraPixels: extraPixels,
      visibleSlidesCount: visibleSlidesCount,
      isReadyToRender: isReadyToRender
    }
  }), children);
};
process.env.NODE_ENV !== "production" ? Carousel.propTypes = {
  /**
   * Gutter between slides
   */
  gutter: PropTypes.number.isRequired,
  /**
   * Minimum width for each slide
   */
  minSlideWidth: PropTypes.number.isRequired,
  /**
   * Max width for each slide
   */
  maxSlideWidth: PropTypes.number.isRequired,
  /**
   * Render the previous button
   */
  renderPrevButton: PropTypes.func,
  /**
   * Render the next button
   */
  renderNextButton: PropTypes.func,
  /**
   * Function to dictate any responsive behavior in the carousel.
   */
  responsive: PropTypes.func,
  /**
   * Slides. Each child is wrapped in a slide container.
   */
  children: PropTypes.node,
  /**
   * Classes
   */
  classes: classesPropType(['relativeContainer', 'container', 'slide', 'slideContainer', 'slidesList', 'prevButton', 'nextButton', 'isLeftAligned', 'isRightAligned', 'isCenterAligned']),
  /**
   * Override carousel container id
   */
  id: PropTypes.string,
  /**
   * Override ID of screen reader description element
   */
  srDescriptionID: PropTypes.string,
  /**
   * A mapper function that receives the navigation state and returns 'left', 'center', 'right'
   */
  alignment: PropTypes.func,
  /**
   * Human terms used to descrive the carousel and slides. This is to provide a better screen reader experience
   *
   * Examples:
   * ```json
   *  {
   *     "carousel": "Product catalog",
   *     "slides": {"plural": "Products", "singular": "Product"}
   *  }
   * ```
   */
  terms: humanTermType(['slides', 'carousel']),
  /**
   * Page size used to determine how many slides to move on left and right button clicks. When set to `'auto'` it is dynamically adjusted to be the number of visible slides
   */
  pagination: PropTypes.oneOfType([PropTypes.oneOf(['auto']), PropTypes.number]),
  /**
   * Callback called before the navigation index changes
   *
   * This can be due to user dragging the slides, clicking on the navigation buttons,
   * or even resizing the screen.
   */
  onNavigationIndexChange: PropTypes.func,
  /**
   * Callback called after carousel determines the navigation bounds/range of the carousel also
   * known as the min and max index allowed, while respecting the visual requirements of the carousel
   * (not allowing empty spaces on the left or right side of the carousel)
   */
  onNavigationRangeChange: PropTypes.func,
  /**
   * Number to control the navigation index
   */
  navigationIndex: PropTypes.number,
  /**
   * The initial value for the navigation index on uncontrolled mode
   */
  navigationIndexDefaultValue: PropTypes.number,
  /**
   * Aria role to assign to slide. "group" is the default, and should be used in most general cases. "tabpanel" should be used when every other slide is not visible, additionally, if there is are tabs used to control the slides. See example here:
   *
   *  https://w3c.github.io/aria-practices/examples/carousel/carousel-2-tablist.html
   */
  slideRole: PropTypes.oneOf(['group', 'tabpanel']),
  /**
   * Aria role to assign to carousel. "group" is the default. This should be used when multiple instances of the carousel will be rendered on the page. However, you can/should use "region" when the carousel takes up a significant amount of space on the page, or when you have a single carousel on the page and want to identify it as a major landmark.
   */
  carouselRole: PropTypes.oneOf(['group', 'region']),
  /**
   * Aria role assignable to list. By default role is "list" but it becomes "presentation" when there is only one item visible. You can override this behavior by specifying the role for this element
   */
  listRole: PropTypes.oneOf(['presentation', 'list']),
  /**
   * Override prefix used for each slide id
   */
  slideIDPrefix: PropTypes.string
} : void 0;
Carousel.defaultProps = {
  slideRole: 'group',
  carouselRole: 'group',
  pagination: 1,
  renderPrevButton: function renderPrevButton() {
    return null;
  },
  renderNextButton: function renderNextButton() {
    return null;
  },
  alignment: getDefaultAlignment,
  responsive: function responsive() {
    return {};
  },
  terms: {
    slides: {
      plural: 'slides',
      singular: 'slide'
    },
    carousel: 'carousel'
  },
  onNavigationIndexChange: noop,
  onNavigationRangeChange: noop
};
export default Carousel;